import { Button, Col, Form, Row, Switch, message } from "antd";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AttributeBox from "../../../commons/components/AttributeBox/AttributeBox";
import BorderGradientButton from "../../../commons/components/BorderGradientButton/BorderGradientButton";
import CustomDropImageInput from "../../../commons/components/CustomDropImageInput/CustomDropImageInput";
import CustomInput from "../../../commons/components/CustomInput/CustomInput";
import { CustomSelect } from "../../../commons/components/CustomSelect/CustomSelect";
import FormBox from "../../../commons/components/FormBox/FormBox";
import FormHeader from "../../../commons/components/FormHeader/FormHeader";
import GradientText from "../../../commons/components/GradientText/GradientText";
import TextArea from "../../../commons/components/TextArea/TextArea";
import { DISCOUNT_PERCENTAGE_OPTIONS } from "../../../commons/constants/product";
import defaultAxios from "../../../commons/utils/axios";
import { decimalOnlyInput, filterECharacterInputDecimal } from "../../../commons/utils/functions/integerOnly";
import { calculateCostPrice, calculateSellingPrice } from "../../../commons/utils/functions/productMapping";
import removeExtraSpace from "../../../commons/utils/functions/removeExtraSpace";
import { uploadImageToServer } from "../../../commons/utils/functions/uploadImageToServer";
import { isPositiveNumber, isPositiveNumberOrZero } from "../../../commons/utils/functions/validation";
import { AppContext } from "../../../contexts/AppContext";
import styles from "./CreateUpdateSurplusProductForm.module.scss";

import dayjs from "dayjs";
import TimeInput from "../../../commons/components/TimeInput/TimeInput";
import { LETTER_REGEX_ALLOW_DECIMAL, MediaDirectoryEnum, StoreExample } from "../../../commons/constants";
import {
  CREATE_PRODUCT_SURPLUS_URL,
  GET_RESERVATION_QUANTITY,
  MARKUP_FEES,
  UPDATE_PRODUCT_SURPLUS_URL,
} from "../../../commons/constants/api-urls";
import { routerNavigation } from "../../../commons/constants/routers";
import {
  createNewSurplusAttribute,
  getBodyForSurplusProduct,
  getValuesFromSurplusProductDetail,
  mappingSurplusAttributeWithOldAttributeAfterEdit,
} from "../../../commons/utils/functions/surplusMapping";
import PreviewProduct from "../../PreviewProduct/PreviewProduct";
import { SURPLUS_ERROR, SURPLUS_STATUS } from "../../../commons/constants/surplus";
import { checkTimeWithCurrentTime } from "../../../commons/utils/functions/formatDate";
import { STORE_TYPE } from "../../../commons/constants/store";
import { CustomStatus } from "../../../commons/components/CustomStatus/CustomStatus";
import ConfirmPopup from "../../../commons/components/ConfirmPopup/ConfirmPopup";
import { checkDuplicateVariantData } from "../../../commons/utils/functions/helper";
import { MODES } from "../../../commons/constants/user";

type Props = {
  surplus?: Surplus | null;
};

const CreateUpdateSurplusProductForm = ({ surplus }: Props) => {
  const { user, currentStore, configAttributes } = useContext(AppContext);

  const navigate = useNavigate();
  const [form] = Form.useForm<CreateUpdateSurplusProductValues>();
  const [markup, setMarkup] = useState(0);
  const [preview, setPreview] = useState<boolean>(false);
  const [isFormLoad, setIsFormLoad] = useState<boolean>(false);
  const [isMatchRatio, setIsMatchRatio] = useState<boolean[]>([]);
  const sellingPrice = Form.useWatch("sellingPrice", form);
  const costPrice = Form.useWatch("costPrice", form);
  const [loading, setLoading] = useState<"save" | false>(false);
  const [surplusProductPreview, setSurplusProductPreview] = useState<CreateUpdateSurplusProductBody | null>(null);
  const [isSellingMode, setIsSellingMode] = useState(false);
  const [openConfirmPopup, setOpenConfirmPopup] = useState(false);
  const [descriptionConfirmPopup, setDescriptionConfirmPopup] = useState("");
  const oldStatus: string | undefined = surplus ? surplus.status : undefined;
  const isActiveSurplus =
    surplus && surplus.status === SURPLUS_STATUS.ACTIVE ? checkTimeWithCurrentTime(surplus.endTime as string) : null;

  async function handleGetMarkup() {
    try {
      const res = await defaultAxios.get(MARKUP_FEES(currentStore?.id));
      setMarkup(res.data.mark_up);
    } catch (error) {
      message.error("Get markup failed");
    }
  }

  async function checkAvailableQuantity(surplusId: string, availableQuantity: number) {
    if (!surplusId) return true;
    try {
      const res = await defaultAxios.get(GET_RESERVATION_QUANTITY(currentStore?.id, surplusId));
      // reservation quantity is not valid
      if (res && res.data && res.data.reservationQuantity > availableQuantity) {
        message.error(
          `Stock cannot be set below the reserved quantity (${res.data.reservationQuantity}) to prevent overselling.`
        );
        setLoading(false);
        return false;
      }
      return true;
    } catch (error) {
      setLoading(false);
      return true;
    }
  }

  async function createNewSurplusProduct(body: CreateUpdateSurplusProductBody) {
    try {
      const realBody = {
        ...body,
      };
      // create new change
      await defaultAxios.post<CreateUpdateSurplusProductResponse>(CREATE_PRODUCT_SURPLUS_URL(body.storeId), realBody);
      message.success("Create surplus successfully");
      navigate(routerNavigation.STORES.SURPLUS_MANAGEMENT(currentStore.id.toString()));
      setLoading(false);
    } catch (error: any) {
      let errorMessage = "Create surplus failed";
      if (error?.response?.data?.error?.message === SURPLUS_ERROR.code) {
        errorMessage = SURPLUS_ERROR.message;
      }

      message.error(errorMessage);
      setLoading(false);
    }
  }

  async function editActiveSurplusProduct(body: CreateUpdateSurplusProductBody) {
    try {
      const realBody = {
        ...body,
        status: oldStatus,
        id: (surplus as any)?.id,
      };
      await defaultAxios.put<CreateUpdateProductResponse>(
        UPDATE_PRODUCT_SURPLUS_URL(currentStore.id, (surplus as any)?.id),
        realBody
      );
      setTimeout(() => {
        message.success("Update surplus successfully");
        setLoading(false);
        navigate(routerNavigation.STORES.SURPLUS_MANAGEMENT(currentStore.id.toString()));
      }, 3000);
    } catch (error) {
      setLoading(false);
      message.error(`Update surplus failed`);
    }
  }

  const onFinish = async (values: CreateUpdateSurplusProductValues, haveShownConfirm: boolean = false) => {
    if (!currentStore) {
      message.error(`Please select store`);
      return;
    }
    let attributes = [...values.attributes];

    const { isDuplicateAttr, isDuplicateVariant, warningDes } = checkDuplicateVariantData(attributes);
    if (isDuplicateAttr) {
      message.error(warningDes);
      return;
    }
    if (!haveShownConfirm && isDuplicateVariant && warningDes) {
      setOpenConfirmPopup(true);
      setDescriptionConfirmPopup(warningDes);
      return;
    }
    setLoading("save");
    const isEdit = surplus?.id;

    if (isEdit) {
      const isValid = await checkAvailableQuantity(surplus?.id, values.availableQuantity);
      if (!isValid) {
        return;
      }
    }

    const { images } = values;
    const imageUrls = images.filter(image => typeof image === "string") as string[];

    const newImages = images.filter(image => typeof image !== "string" && !Array.isArray(image));

    if (newImages) {
      try {
        await uploadImageToServer(newImages, MediaDirectoryEnum.PRODUCT).then(res => {
          if (Array.isArray(res)) {
            res.forEach(item => imageUrls.push(item));
          } else {
            imageUrls.push(res);
          }
        });
      } catch (error) {
        setLoading(false);
        message.error("Upload image failed");
      }
    }

    if (isEdit) {
      attributes = mappingSurplusAttributeWithOldAttributeAfterEdit(surplus.attributes, values.attributes);
    }
    values.attributes = attributes;

    try {
      const body: CreateUpdateSurplusProductBody = {
        ...getBodyForSurplusProduct(values),
        isSellingMode,
        storeId: currentStore.id,
        imageUrl: Array.isArray(imageUrls) && imageUrls.length > 0 ? imageUrls[0] : imageUrls,
        thumbnail: imageUrls[0],
        groupMenuId: values.groupMenuId ? values.groupMenuId : undefined,
      };

      if (isEdit) {
        editActiveSurplusProduct(body);
      } else {
        createNewSurplusProduct(body);
      }
    } catch (error: any) {
      console.log(error);
      message.error(`${surplus?.id ? "Update" : "Create"} surplus failed`);
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    const namePath = errorInfo.errorFields[0].name;
    form.getFieldInstance(namePath)?.focus();
  };

  function onChangeSwitch(checked: boolean) {
    setIsSellingMode(checked);
  }

  function handleFormatPrice(price: number | string, key: string) {
    if (!price) return;

    if (isNaN(Number(price))) {
      //stupid typescript, try to remove Number to see the result
      form.setFieldValue(key, undefined);
      return;
    }

    if (Number(price) === 0) {
      form.setFieldValue(key, Number(price).toFixed(2));
    }

    const priceAfterDecimal = price.toString().split(".");

    if (priceAfterDecimal[1]?.length > 2) {
      form.setFieldValue(key, (Math.floor(Number(price) * 100) / 100).toFixed(2));
    } else {
      form.setFieldValue(key, Number(price).toFixed(2));
    }

    if (Number(price) > 0) {
      form.setFields([{ name: key, errors: undefined }]);
    }
  }

  const handleOnChangeInputNumberCostPrice = (name: string, event: any) => {
    const costPrice = event.target.value && event.target.value.replace(LETTER_REGEX_ALLOW_DECIMAL, "");
    form.setFieldValue(name, costPrice);
    form.setFieldValue("sellingPrice", calculateSellingPrice(costPrice, markup));
    form.validateFields(["sellingPrice"]);
  };

  const handleOnChangeInputNumberSellingPrice = (name: string, event: any) => {
    const sellingPrice = event.target.value && event.target.value.replace(LETTER_REGEX_ALLOW_DECIMAL, "");
    form.setFieldValue(name, sellingPrice);
    form.setFieldValue("costPrice", calculateCostPrice(sellingPrice, markup));
    form.validateFields(["costPrice"]);
  };

  const handleCancelModal = () => {
    setOpenConfirmPopup(false);
    setDescriptionConfirmPopup("");
  };

  useEffect(() => {
    if (isFormLoad || !surplus || !markup) return;

    form.setFieldsValue(getValuesFromSurplusProductDetail(markup, surplus, currentStore?.id, configAttributes));

    setIsFormLoad(true);
  }, [surplus, currentStore?.id, form, configAttributes, markup]);

  useEffect(() => {
    if (currentStore.id && currentStore.id !== StoreExample.id) {
      handleGetMarkup();
    }
  }, [currentStore]);

  return (
    <>
      <Form
        form={form}
        name="create-update-surplus"
        wrapperCol={{ span: 24 }}
        initialValues={getValuesFromSurplusProductDetail(markup, surplus, user?.merchantIds?.[0])}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
      >
        <FormHeader
          title={
            <div className={styles.titleWrapper}>
              <div>{surplus?.name ?? "Add new surplus product"}</div>
              {surplus?.id && (
                <CustomStatus
                  label={isActiveSurplus ? "Active" : surplus.isSoldOut ? "Sold out" : "Expired"}
                  className={isActiveSurplus ? styles.activeStatus : styles.expiredStatus}
                />
              )}
            </div>
          }
          actions={
            <>
              <Button type="text" className={styles.cancel} onClick={() => navigate(-1)}>
                Cancel
              </Button>
              {/* <Button
                type="text"
                className={styles.preview}
                onClick={() => {
                  setSurplusProductPreview(getBodyForSurplusProduct(form.getFieldsValue()));
                  setPreview(true);
                }}
              >
                <GradientText text="Preview" />
              </Button> */}

              {user?.merchant_role?.id !== MODES.USER && (
                <Button
                  type="primary"
                  htmlType="submit"
                  className={styles.save}
                  disabled={!!loading}
                  loading={loading === "save"}
                >
                  {loading
                    ? "Submitting"
                    : !surplus?.id || isActiveSurplus || surplus?.isSoldOut
                    ? "Submit"
                    : "Reactive"}
                </Button>
              )}
            </>
          }
        />

        <FormBox
          title={
            <div className={styles.formToggleBox}>
              <p>Basic information</p>
            </div>
          }
        >
          <CustomInput
            name="name"
            label="Product name"
            placeholder="Enter product name"
            onBlur={removeExtraSpace("name", form)}
            rules={[
              {
                required: true,
                message: "This field is required",
                whitespace: true,
              },
            ]}
            maxLength={120}
          />
          <CustomDropImageInput
            name="images"
            label="Product image"
            square
            aspect={1}
            description={
              <>
                <p>Up to 10 images</p>
                <div style={{ marginTop: "12px", lineHeight: "12px" }}> Image ratio will be 1:1</div>
              </>
            }
            onError={err => form?.setFields([{ name: "images", errors: [err] }])}
            setIsMatchRatio={setIsMatchRatio}
            isMatchRatio={isMatchRatio}
            rules={[
              { required: false, message: "This field is required" },
              {
                validator: (_, value) => {
                  if (isMatchRatio.every(item => item === true)) {
                    return Promise.resolve();
                  } else {
                    return Promise.reject("Image aspect ratio must be 3:2");
                  }
                },
              },
            ]}
            isEdit={surplus?.id ? true : false}
            form={form}
            isCheckRation={false}
          />

          <TextArea
            name="description"
            label="Description"
            onBlur={removeExtraSpace("description", form)}
            placeholder="Tell us something about your surplus"
            rules={[
              {
                required: true,
                message: "This field is required",
                whitespace: true,
              },
            ]}
            showCount
            maxLength={3000}
          />
        </FormBox>
        <FormBox
          title={
            <div className={styles.formToggleBox}>
              <p>
                Selling information{" "}
                <Switch
                  style={{ marginLeft: "15px" }}
                  checked={isSellingMode}
                  onChange={onChangeSwitch}
                  checkedChildren="Selling Mode"
                  unCheckedChildren="Cost Mode"
                />
              </p>
              <p>Mark Up: {markup + "%"} </p>
            </div>
          }
        >
          <Form.Item dependencies={["attributes"]}>
            {({ getFieldValue }) => {
              return (
                <Row gutter={24}>
                  <Col xs={12} sm={12}>
                    <CustomInput
                      type="text"
                      name="costPrice"
                      label="Product Cost Price (Amount due to Merchant)"
                      placeholder="0.00"
                      readOnly={isSellingMode ? true : false}
                      rules={[
                        { required: true, message: "This field is required" },
                        { validator: isPositiveNumberOrZero("costPrice") },
                      ]}
                      onKeyDown={event => {
                        decimalOnlyInput(event);
                      }}
                      onChange={event => {
                        filterECharacterInputDecimal("costPrice", form, event);
                        handleOnChangeInputNumberCostPrice("costPrice", event);
                      }}
                      onBlur={() => {
                        handleFormatPrice(costPrice, "costPrice");
                      }}
                    />
                  </Col>
                  <Col xs={12} sm={12}>
                    <CustomInput
                      type="text"
                      name="sellingPrice"
                      label="Product Selling Price"
                      placeholder="0.00"
                      readOnly={isSellingMode ? false : true}
                      rules={[
                        { required: true, message: "This field is required" },
                        { validator: isPositiveNumberOrZero("sellingPrice") },
                      ]}
                      onKeyDown={event => {
                        decimalOnlyInput(event);
                      }}
                      onChange={event => {
                        filterECharacterInputDecimal("sellingPrice", form, event);
                        handleOnChangeInputNumberSellingPrice("sellingPrice", event);
                      }}
                      onBlur={() => {
                        handleFormatPrice(sellingPrice, "sellingPrice");
                      }}
                    />
                  </Col>
                </Row>
              );
            }}
          </Form.Item>
          <Form.List name="attributes">
            {(
              attributes,
              { add, remove }: FormListOperationCustom<CreateUpdateSurplusProductValues["attributes"][0]>
            ) => {
              return (
                <>
                  {attributes.map(({ key, name, ...restAttribute }) => {
                    return (
                      <AttributeBox
                        key={key}
                        attributeIndex={name}
                        restAttribute={restAttribute}
                        remove={remove}
                        form={form}
                        markup={markup}
                        isSellingMode={isSellingMode}
                        type={STORE_TYPE.SURPLUS}
                      />
                    );
                  })}
                  <div className={styles.addAttribute}>
                    <BorderGradientButton onClick={() => add(createNewSurplusAttribute())}>
                      <GradientText text="Add more attribute" />
                    </BorderGradientButton>
                  </div>
                </>
              );
            }}
          </Form.List>
        </FormBox>

        <FormBox
          title={
            <div className={styles.formToggleBox}>
              <p>Surplus</p>
            </div>
          }
        >
          <Row gutter={24}>
            <Col xs={24} sm={12} md={12} lg={6} span={6}>
              <TimeInput
                label="Start time"
                name="startTime"
                format="HH:mm"
                placeholder="Select start time"
                onChange={() => {
                  if (form.isFieldsTouched(["startTime", "endTime"])) {
                    form.validateFields(["endTime"]);
                  }
                }}
                rules={[
                  {
                    validator: (_, value) => {
                      const endTime = form.getFieldValue("endTime");
                      if (!value || !endTime) return Promise.resolve();

                      if (dayjs(value, "HH:mm").isAfter(dayjs(endTime, "HH:mm"))) {
                        return Promise.reject(new Error("Start time can't be later than end time"));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={12} lg={6} span={6}>
              <TimeInput
                label="End time"
                name="endTime"
                format="HH:mm"
                placeholder="Select end time"
                onChange={() => {
                  if (form.isFieldsTouched(["startTime", "endTime"])) {
                    form.validateFields(["startTime"]);
                  }
                }}
                rules={[
                  {
                    validator: (_, value) => {
                      const startTime = form.getFieldValue("startTime");
                      if (!value || !startTime) return Promise.resolve();

                      if (dayjs(value, "HH:mm").isBefore(dayjs(startTime, "HH:mm"))) {
                        return Promise.reject(new Error("End time can't be earlier than start time"));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={12} lg={6} span={6}>
              <CustomSelect
                name="discountPercentage"
                label="Discount percentage"
                options={DISCOUNT_PERCENTAGE_OPTIONS}
                placeholder="Select discount percentage"
                rules={[
                  {
                    required: true,
                    message: "Please select discount percentage",
                  },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={12} lg={6} span={6}>
              <CustomInput
                name="availableQuantity"
                label="Quantity"
                type="number"
                rules={[
                  {
                    required: true,
                    message: "Please select quantity",
                  },
                  { validator: isPositiveNumber("Quantity") },
                ]}
                placeholder="Select quantity"
              />
            </Col>
          </Row>
        </FormBox>
      </Form>

      {surplusProductPreview && (
        <PreviewProduct open={preview} onCancel={() => setPreview(false)} surplus={surplusProductPreview} />
      )}

      <ConfirmPopup
        open={openConfirmPopup}
        onCancel={handleCancelModal}
        title="Do you want to continue?"
        description={descriptionConfirmPopup}
        loading={loading === "save"}
        onConfirm={() => {
          onFinish(form.getFieldsValue(), true);
        }}
      />
    </>
  );
};

export default CreateUpdateSurplusProductForm;
